import { createTUID, isGroundElement, PcbNodeTypes, } from "@buildwithflux/models";
import { brandedValues, silentLogger } from "@buildwithflux/shared";
import { applyChange } from "deep-diff";
import { minBy, pickBy, values } from "lodash";
import { vec2 } from "../../../common/geometry/vec2";
import { ElementHelper } from "../../../elements";
import { removeNodesAndDescendants } from "../../../nodes";
import { PcbLayoutRuleCompiler, PcbTreeManager } from "../../../pcb";
import { isTerminalEqual, Wiring } from "../../../wiring";
import { connectPortalNearPin, costBasedAutoWire, getFirstTerminal } from "../../../wiring/costBasedAutoWire";
import { ensureRouteVertexState } from "../../ensureRouteVertexState";
/**
 * Handles the `updateNetsLabel` redux action by mutating a net's
 * label
 */
export function handleUpdateNetsLabel(draftState, netLabelMap) {
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.entries(netLabelMap).forEach(([netId, label]) => {
        const net = draftState.nets[netId];
        if (net) {
            net.label = label;
        }
        // Update the node label as well
        const node = draftState.pcbLayoutNodes[netId];
        if (node) {
            node.name = label;
        }
    });
}
/** Return the provided list as pairs of [current, next]. The last element is not included in the
 * iteration (it is included as the `next` of the second-to-last element), so that both `current`
 * and `next` are always defined.
 */
function pairs(array) {
    return array.slice(0, -1).map((v, idx) => [v, array[idx + 1]]);
}
/** Given a copilot-ish pin designator like "GND", "GND:GND", "GND:P1" or "R1:P1", determine whether
 * it looks like a connection to ground (not the ground pin of a component, but to the ground net)
 */
export function isGroundPinDescriptor(pin) {
    return /\b(gnd|ground)\b/i.test(pin.component);
}
export function getTerminalFromPinDescriptor(doc, pin) {
    const elements = doc.elements;
    const element = values(elements).find((element) => element.label?.toLowerCase().trim() === pin.component.toLowerCase().trim());
    const terminal = values(element?.part_version_data_cache?.terminals ?? []).find((terminal) => (pin.pinDesignator && terminal.name === pin.pinDesignator) ||
        terminal.pin_number === pin.pinDesignator ||
        (pin.pinNumber && terminal.pin_number === pin.pinNumber));
    if (!element || !terminal) {
        // This may be an unknown terminal, or it may be a ground-like pin descriptor.
        // Either way return undefined: for ground-like pins, the caller will find or
        // create an appropriate ground element to connect to.
        return;
    }
    return {
        element_uid: element.uid,
        terminal_uid: terminal.uid,
    };
}
export function connectNetsByDescriptor(document, netConnections, actionContext) {
    const wiring = new Wiring(document.routeVertices, document.elements);
    const groundElements = values(pickBy(document.elements, (e) => ElementHelper.isGroundElement(e)));
    const groundConnectionSites = groundElements.map((element) => wiring.getWireConnectionSite({ terminal: getFirstTerminal(element) }));
    const netNameHints = {};
    for (const { netName, pins, isExplicitNetName } of netConnections) {
        // New Wiring instance each iteration, because the document may change between iterations
        // TODO would be better to be able to update it live; we could theoretically do that without much trouble.
        const wiring = new Wiring(document.routeVertices, document.elements);
        const isGroundNet = pins.some((p) => isGroundPinDescriptor(p)); // TODO we prob want to be smarter here and also detect if any of the pins already belong to a ground-connected net.
        const knownTerminals = pins.map((p) => getTerminalFromPinDescriptor(document, p)).filter((t) => !!t);
        const pinSites = knownTerminals.map((t) => wiring.getWireConnectionSite({ terminal: t }));
        const centroid = vec2.centroid(pinSites.map((s) => s.position));
        let closestGround = minBy(groundConnectionSites, (g) => vec2.distance(g.position, centroid));
        if (isGroundNet && !closestGround && knownTerminals[0]) {
            // There is no existing ground. Create one near one of the terminals.
            const newGround = connectPortalNearPin(document, "GND", knownTerminals[0], true, actionContext);
            closestGround = wiring.getWireConnectionSite({ terminal: getFirstTerminal(newGround) });
            // Make it available for subsequent iterations
            groundConnectionSites.push(closestGround);
        }
        // If the LLM provides a "ground-like" pin specifier, and we can't find an exact match,
        // then match it to the nearest ground.
        const terminals = pins
            .map((pin) => {
            const terminal = getTerminalFromPinDescriptor(document, pin);
            if (terminal)
                return terminal;
            if (isGroundPinDescriptor(pin)) {
                return closestGround?.terminal;
            }
        })
            .filter((t) => !!t);
        // If we have exactly 1 terminal and an explicit named net (from ^Net syntax),
        // create a portal so there's something to wire to. This handles e.g. ["BT1:N", "^VCC"].
        if (terminals.length === 1 && isExplicitNetName && terminals[0]) {
            const isGround = /\b(gnd|ground)\b/i.test(netName);
            const newPortal = connectPortalNearPin(document, netName, terminals[0], isGround, actionContext);
            terminals.push(getFirstTerminal(newPortal));
        }
        terminals.map((t) => (netNameHints[createTUID(t)] = netName));
        const segments = pairs(terminals);
        costBasedAutoWire(document, segments.map((s) => ({ start: s[0], end: s[1] })), isGroundNet, actionContext);
    }
    ensureRouteVertexState(document);
    // Lastly we need to update the nets
    // TODO see above re `silentLogger`
    const pcbDomTreeManager = new PcbTreeManager(document.pcbLayoutNodes, silentLogger);
    pcbDomTreeManager.updateNetNodes(document, { netNameHints });
    document.pcbLayoutNodes = pcbDomTreeManager.treeNodes;
}
function deletePortalElements(document, portalUids) {
    const { elements, pcbLayoutNodes, pcbLayoutRuleSets } = document;
    const nodeUidsToRemove = portalUids.filter((uid) => {
        const element = elements[uid];
        return !!element && !isGroundElement(element);
    });
    removeNodesAndDescendants(pcbLayoutNodes, nodeUidsToRemove);
    for (const elementUid of portalUids) {
        const element = elements[elementUid];
        if (!element)
            continue;
        delete elements[elementUid];
        const nodeSelector = PcbLayoutRuleCompiler.createUidAttributeSelector(PcbNodeTypes.element, elementUid);
        const ruleSet = PcbLayoutRuleCompiler.getRuleSetBasedOnSelector(nodeSelector, pcbLayoutRuleSets);
        if (ruleSet) {
            delete pcbLayoutRuleSets[ruleSet.uid];
        }
    }
}
/** Disconnect the given pins, specified by their human-friendly descriptors. Mutates the passed-in document. */
export function disconnectPinsByDescriptor(document, pins) {
    const wiring = new Wiring(document.routeVertices, document.elements);
    const elements = document.elements;
    const terminalsToDisconnect = pins.map((pin) => getTerminalFromPinDescriptor(document, pin)).filter((t) => !!t);
    const documentVertices = document.routeVertices || {};
    const vertexUids = terminalsToDisconnect.flatMap((terminal) => {
        const vertices = brandedValues(documentVertices)
            .filter((v) => v.terminal && isTerminalEqual(v.terminal, terminal))
            .map((v) => v.id);
        return vertices;
    });
    if (!vertexUids.length)
        return;
    const verticesToRemoveSet = new Set();
    const verticesToKeepSet = new Set();
    const searchStack = [];
    const visitedSet = new Set();
    for (const vertexUid of vertexUids) {
        verticesToRemoveSet.add(vertexUid);
    }
    for (const vertex of Object.values(documentVertices || {})) {
        if (!verticesToRemoveSet.has(vertex.id) && vertex.terminal) {
            const element = elements[vertex.terminal.element_uid];
            if (element && ElementHelper.isPortal(element))
                continue; // Don't track portal terminals
            verticesToKeepSet.add(vertex.id);
        }
    }
    for (const vertexUid of Array.from(verticesToRemoveSet)) {
        const wires = wiring.getDirectlyConnectedWires(vertexUid);
        for (const wire of wires) {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            wire.slice(0, -1).forEach((v) => verticesToRemoveSet.add(v));
            const lastVertex = wire[wire.length - 1];
            if (lastVertex && !verticesToKeepSet.has(lastVertex) && !verticesToRemoveSet.has(lastVertex)) {
                searchStack.push(lastVertex);
            }
        }
    }
    while (searchStack.length > 0) {
        const vertexUid = searchStack.pop();
        if (!vertexUid)
            continue; // impossible
        const vertex = wiring.vertices.get(vertexUid);
        if (!vertex)
            continue;
        if (visitedSet.has(vertexUid))
            continue;
        visitedSet.add(vertexUid);
        const wires = wiring.getDirectlyConnectedWires(vertexUid);
        let nonRemovalCount = 0;
        for (const wire of wires) {
            const lastVertex = wire[wire.length - 1];
            if (lastVertex && !verticesToRemoveSet.has(lastVertex)) {
                nonRemovalCount++;
            }
        }
        for (const wire of wires) {
            const lastVertex = wire[wire.length - 1];
            if (!lastVertex)
                continue;
            if ((nonRemovalCount > 1 || (nonRemovalCount === 1 && vertex.terminal)) &&
                verticesToKeepSet.has(lastVertex)) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                wire.forEach((vId) => verticesToKeepSet.add(vId));
            }
            else if (((nonRemovalCount === 1 && !vertex.terminal) || (nonRemovalCount === 0 && vertex.terminal)) &&
                verticesToRemoveSet.has(lastVertex)) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                wire.forEach((vId) => verticesToRemoveSet.add(vId));
            }
            else if (!verticesToRemoveSet.has(lastVertex) && !verticesToKeepSet.has(lastVertex)) {
                searchStack.push(lastVertex);
            }
        }
    }
    const affectedPortalUids = new Set();
    for (const vertexUid of verticesToRemoveSet) {
        const vertex = documentVertices[vertexUid];
        if (!vertex)
            continue;
        if (vertex.terminal?.element_uid) {
            const element = elements[vertex.terminal.element_uid];
            if (element && ElementHelper.isPortalOrGroundElement(element)) {
                affectedPortalUids.add(element.uid);
            }
        }
        for (const neighborUid of Object.keys(vertex.connectedVertices ?? {})) {
            const neighbor = documentVertices[neighborUid];
            if (neighbor?.terminal?.element_uid) {
                const element = elements[neighbor.terminal.element_uid];
                if (element && ElementHelper.isPortalOrGroundElement(element)) {
                    affectedPortalUids.add(element.uid);
                }
            }
        }
    }
    const vertexIdsToRemove = [...verticesToRemoveSet];
    wiring.removeVertices(vertexIdsToRemove);
    const vertexUpdates = wiring.getVertexUpdates();
    for (const update of vertexUpdates) {
        applyChange(document.routeVertices, true, update);
    }
    if (affectedPortalUids.size > 0) {
        const remainingConnections = new Set();
        for (const vertex of Object.values(document.routeVertices ?? {})) {
            if (vertex.terminal?.element_uid && affectedPortalUids.has(vertex.terminal.element_uid)) {
                if (Object.keys(vertex.connectedVertices ?? {}).length > 0) {
                    remainingConnections.add(vertex.terminal.element_uid);
                }
            }
        }
        const orphanedPortalUids = [];
        for (const portalUid of affectedPortalUids) {
            if (!remainingConnections.has(portalUid)) {
                orphanedPortalUids.push(portalUid);
            }
        }
        if (orphanedPortalUids.length > 0) {
            deletePortalElements(document, orphanedPortalUids);
            const orphanedSet = new Set(orphanedPortalUids);
            for (const [vid, vertex] of Object.entries(document.routeVertices ?? {})) {
                if (vertex.terminal?.element_uid && orphanedSet.has(vertex.terminal.element_uid)) {
                    delete document.routeVertices[vid];
                }
            }
        }
    }
    if (vertexUpdates.length > 0) {
        // TODO `silentLogger` here is probably not great, but I also don't love that we log
        // a lot of noise with PcbTreeManager, which we basically ignore, and also that we
        // have to param-drill the logger through so many layers of function calls.
        const pcbDomTreeManager = new PcbTreeManager(document.pcbLayoutNodes, silentLogger);
        pcbDomTreeManager.updateNetNodes(document, { deleteNodesOnly: true });
        document.pcbLayoutNodes = pcbDomTreeManager.treeNodes;
    }
}
/**
 * Identifies and removes ALL portal/ground elements that have zero wire connections.
 * A portal is considered orphaned if no vertices in routeVertices reference its terminal.
 *
 * NOTE: This is a global garbage collector. Prefer scoped cleanup (as done in
 * disconnectPinsByDescriptor) for operations where we know which portals are directly
 * affected. This function is intended for user-triggered or session-scoped cleanup only.
 *
 * This function mutates the document in place.
 */
export function cleanupOrphanedPortals(document, logger) {
    const { elements, routeVertices } = document;
    const elementsWithConnections = new Set();
    for (const vertex of Object.values(routeVertices ?? {})) {
        if (vertex.terminal?.element_uid) {
            if (Object.keys(vertex.connectedVertices ?? {}).length > 0) {
                elementsWithConnections.add(vertex.terminal.element_uid);
            }
        }
    }
    const orphanedPortalUids = [];
    for (const element of Object.values(elements)) {
        if (ElementHelper.isPortalOrGroundElement(element)) {
            if (!elementsWithConnections.has(element.uid)) {
                orphanedPortalUids.push(element.uid);
            }
        }
    }
    if (orphanedPortalUids.length === 0) {
        return;
    }
    deletePortalElements(document, orphanedPortalUids);
    const orphanedSet = new Set(orphanedPortalUids);
    for (const [vid, vertex] of Object.entries(document.routeVertices ?? {})) {
        if (vertex.terminal?.element_uid && orphanedSet.has(vertex.terminal.element_uid)) {
            delete document.routeVertices[vid];
        }
    }
    const pcbTreeManager = new PcbTreeManager(document.pcbLayoutNodes, logger);
    pcbTreeManager.updateNetNodes(document, { deleteNodesOnly: true });
    document.pcbLayoutNodes = pcbTreeManager.treeNodes;
}
//# sourceMappingURL=reducer.js.map